<template>
  <van-popup v-model:show="dialogVisible" position="bottom" round :style="{ height: '85%' }">
    <div class="repair-extend-dialog">
      <div class="re-header">
        <div class="re-close" @click="dialogVisible = false">
          <van-icon name="cross" />
        </div>
        <div class="re-title">{{ isEdit ? "编辑维修记录" : "添加维修记录" }}</div>
        <div class="re-placeholder"></div>
      </div>
      <div class="re-body">
        <van-cell-group>
          <!-- 开始时间 -->
          <van-field
            :model-value="form.start_time"
            is-link
            readonly
            required
            label="开始时间"
            placeholder="选择日期时间"
            @click="showStartPicker = true"
          />

          <!-- 结束时间 -->
          <van-field
            :model-value="form.end_time"
            is-link
            readonly
            required
            label="结束时间"
            placeholder="选择日期时间"
            @click="showEndPicker = true"
          />

          <!-- 是否解决 -->
          <van-field label="是否解决">
            <template #right-icon>
              <van-switch
                :model-value="form.is_resolved === 1"
                @update:model-value="(v) => (form.is_resolved = v ? 1 : 0)"
                size="22px"
              />
            </template>
          </van-field>

          <!-- 维修工时：根据起止时间自动计算 -->
          <van-field :model-value="computedRepairHours + ' 小时'" label="维修工时" readonly />

          <!-- 故障分析 -->
          <van-field
            v-model="form.fault_analysis"
            type="textarea"
            rows="3"
            autosize
            required
            label="故障分析"
            placeholder="请输入故障分析"
            maxlength="255"
            show-word-limit
          />
          <van-field
            v-model="form.repair_content"
            type="textarea"
            rows="3"
            autosize
            required
            label="维修内容"
            placeholder="请输入维修内容"
            maxlength="255"
            show-word-limit
          />
          <van-field
            v-model="form.other_cost_desc"
            type="textarea"
            rows="2"
            autosize
            label="其他成本内容"
            placeholder="请输入其他成本内容"
            maxlength="255"
            show-word-limit
          />
          <van-field
            v-model="form.other_cost_amount"
            type="text"
            inputmode="decimal"
            label="其他成本金额"
            placeholder="0.00"
            @blur="onAmountBlur"
          >
            <template #suffix>元</template>
          </van-field>
          <van-field
            v-model="form.improve_suggestion"
            type="textarea"
            rows="2"
            autosize
            label="改进建议"
            placeholder="请输入改进建议"
            maxlength="255"
            show-word-limit
          />
          <van-field
            v-model="form.remark"
            type="textarea"
            rows="2"
            autosize
            label="备注"
            placeholder="请输入备注"
            maxlength="255"
            show-word-limit
          />
        </van-cell-group>

        <!-- 附件上传：编辑时已有附件与新上传文件统一在 uploader 中展示、删除 -->
        <div class="re-upload-row">
          <span class="re-upload-label">
            附件
            <van-loading v-if="fileLoading" size="12px" class="re-file-loading" />
          </span>
          <van-uploader
            v-model="fileList"
            :after-read="onUploadFile"
            :max-count="9"
            :max-size="10 * 1024 * 1024"
            :multiple="true"
            :deletable="true"
            :show-upload="true"
            :preview-image="true"
            :preview-full-image="true"
            accept="*"
            @oversize="() => Toast('文件大小不能超过 10MB')"
            @delete="onDeleteFile"
          >
            <template #preview-cover="file">
              <div v-if="!file.isImage" class="re-file-name">{{ file.file_name }}</div>
            </template>
          </van-uploader>
        </div>
      </div>

      <div class="re-footer">
        <van-button block plain type="default" :disabled="submitting" @click="dialogVisible = false">取消</van-button>
        <van-button block type="primary" :loading="submitting" @click="onSubmit">确定</van-button>
      </div>
    </div>

    <!-- 开始时间选择器 -->
    <van-popup v-model:show="showStartPicker" position="bottom">
      <van-datetime-picker
        v-model="startPickerValue"
        type="datetime"
        :min-date="minDate"
        @confirm="onStartConfirm"
        @cancel="showStartPicker = false"
      />
    </van-popup>

    <!-- 结束时间选择器 -->
    <van-popup v-model:show="showEndPicker" position="bottom">
      <van-datetime-picker
        v-model="endPickerValue"
        type="datetime"
        :min-date="startPickerValue || minDate"
        @confirm="onEndConfirm"
        @cancel="showEndPicker = false"
      />
    </van-popup>

  </van-popup>
</template>

<script>
import { ref, reactive, watch, computed } from "vue";
import { Toast } from "vant";
import axios from "axios";
import { SaveRepairRecord } from "@/api/ems/eqpInfo";
export default {
  name: "RepairExtendDialog",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    woId: {
      type: [String, Number],
      default: "",
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:show", "confirm"],
  setup(props, { emit }) {
    const dialogVisible = ref(false);
    const showStartPicker = ref(false);
    const showEndPicker = ref(false);
    const fileList = ref([]);
    const fileData = ref([]);//上传的文件数据
    const fileLoading = ref(false);//已有附件加载中
    const IMG_EXTS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tiff", "ico"];
    const createEmptyForm = () => ({
      id: "",
      wo_id: "",
      start_time: "",
      end_time: "",
      is_resolved: 0,
      repair_hours: "",
      fault_analysis: "",
      repair_content: "",
      other_cost_desc: "",
      other_cost_amount: "",
      improve_suggestion: "",
      remark: "",
    });
    const form = reactive(createEmptyForm());
    const isEdit = computed(() => !!(props.initialData && props.initialData.id));

    const startPickerValue = ref(null);
    const endPickerValue = ref(null);
    const minDate = new Date(2020, 0, 1);

    const onAmountBlur = () => {
      const raw = String(form.other_cost_amount || "").trim();
      if (!raw) {
        form.other_cost_amount = "";
        return;
      }
      // 只取数字和第一个小数点，去掉多余的
      let v = raw.replace(/[^\d.]/g, "");
      const firstDot = v.indexOf(".");
      if (firstDot !== -1) {
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
        const [intPart, decPart] = v.split(".");
        if (decPart && decPart.length > 2) {
          v = intPart + "." + decPart.slice(0, 2);
        }
      }
      const num = parseFloat(v);
      if (isNaN(num)) {
        form.other_cost_amount = "";
        return;
      }
      form.other_cost_amount = num.toFixed(2);
    };
//计算维修工时
    const computedRepairHours = computed(() => {
      if (!form.start_time || !form.end_time) return "0";
      const start = new Date(form.start_time);
      const end = new Date(form.end_time);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return "0";
      const hours = (end - start) / 3600000;
      form.repair_hours = Math.round(hours * 100) / 100;
      return form.repair_hours;
    });

    const formatDateTime = (date) => {
      if (!date) return "";
      const d = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const parseDateTime = (str) => {
      if (!str) return null;
      return new Date(str.replace(" ", "T"));
    };

    watch(
      () => props.show,
      (val) => {
        dialogVisible.value = val;
        if (val) {
          const info = props.initialData || {};
          const isEdit = !!info.id;
          Object.assign(form, createEmptyForm(), {
            wo_id: props.woId || info.wo_id || "",
            id: info.id || "",
            start_time: info.start_time ? formatDateTime(info.start_time) : "",
            end_time: info.end_time ? formatDateTime(info.end_time) : "",
            is_resolved: info.is_resolved == 1 ? 1 : 0,
            repair_hours: info.repair_hours ?? "",
            fault_analysis: info.fault_analysis || "",
            repair_content: info.repair_content || "",
            other_cost_desc: info.other_cost_desc || "",
            other_cost_amount: info.other_cost_amount ?? "",
            improve_suggestion: info.improve_suggestion || "",
            remark: info.remark || "",
          });
          startPickerValue.value = parseDateTime(form.start_time);
          endPickerValue.value = parseDateTime(form.end_time);
          // 编辑模式：后端 file_ids 为逗号分隔的附件 id 串
          // 直接拉取附件信息回显到 van-uploader（与新上传文件统一管理）
          const rawFiles = info.file_ids ??  "";
          let ids = [];
            ids = rawFiles.split(",").map((s) => s.trim()).filter(Boolean);
          
          fileList.value = [];
          fileData.value = [];
          if (ids.length) {
            loadExistingFiles(ids);
          }
        } else {
          // 关闭时清空附件状态，避免下次打开残留
          fileLoading.value = false;
          fileList.value = [];
          fileData.value = [];
        }
      }
    );

    watch(dialogVisible, (val) => {
      emit("update:show", val);
    });

    const onStartConfirm = (value) => {
      startPickerValue.value = value;
      form.start_time = formatDateTime(value);
      showStartPicker.value = false;
    };

    const onEndConfirm = (value) => {
      endPickerValue.value = value;
      form.end_time = formatDateTime(value);
      showEndPicker.value = false;
    };

    const onSubmit = () => {
      console.log(form,"测试测试维修录入");
      console.log(fileData.value,"测试测试维修录入eeeeee");
      if (!form.start_time) {
        Toast("请选择开始时间");
        return;
      }
      if (!form.end_time) {
        Toast("请选择结束时间");
        return;
      }
      if (new Date(form.end_time) <= new Date(form.start_time)) {
        Toast("结束时间不能早于或等于开始时间");
        return;
      }
      if (!form.fault_analysis.trim()) {
        Toast("请填写故障分析");
        return;
      }
      if (!form.repair_content.trim()) {
        Toast("请填写维修内容");
        return;
      }
      emit("confirm", { ...form,fileData:fileData.value });
    };
// 上传文件
    const onUploadFile = async (file) => {
      const files = Array.isArray(file) ? file : [file];
      try {
        for (const f of files) {
          const form = new FormData();
          form.append("file", f.file);
          const res = await axios({
            method: "post",
            url: "/oa_portal/oa/portal/Common/UploadFile?sub_system_code=mobileh5",
            data: form,
            headers: {
              "content-type": "multipart/form-data",
              Authorization: localStorage.getItem("Token") || "",
              tenant_id: localStorage.getItem("tenant_id") || "",
            },
          });
          // 接口返回 { success, data: [{ id, file_name, file_extension, file_size, file_path, ... }] }
          const uploaded = res.data && res.data.data ? res.data.data[0] : null;
          if (uploaded && uploaded.id) {
            // 业务数据 push 到 fileData（normalizeFile 做字段映射）
            const normalized = normalizeFile(uploaded);
            fileData.value.push(normalized);
            // 原地补全 van-uploader 条目：id + 遮罩需要的字段（file_type 0=图片）
            f.id = normalized.file_id;
            f.isImage = normalized.file_type === 0;
            f.file_name = normalized.file_name;
          } else {
            f.status = "failed";
            f.message = "上传失败";
          }
        }
        Toast("上传完成");
      } catch (e) {
        console.log(e);
        // 把 van-uploader 内部状态重置：移除失败的那条
        fileList.value = fileList.value.filter(
          (f) => f.status !== "uploading" && f.status !== "failed"
        );
        Toast("上传失败");
      }
    };

    // 删除文件 —— van-uploader @delete 回调，从 fileData 业务数组同步移除
    const onDeleteFile = (file) => {
      const targetId = file.id;
      fileList.value = fileList.value.filter((f) => f.id !== targetId);
      fileData.value = fileData.value.filter((f) => f.file_id !== targetId);
    };

    // 根据扩展名判断文件类型：0-图片，1-文档，2-视频，3-其他
    const getFileType = (ext) => {
      const e = String(ext || "").toLowerCase().replace(/^\./, "");
      if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tiff", "ico"].includes(e)) return 0;
      if (["doc", "docx", "pdf", "txt", "xls", "xlsx", "ppt", "pptx", "rtf", "csv"].includes(e)) return 1;
      if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v", "3gp"].includes(e)) return 2;
      return 3;
    };

    // 接口 UploadFile 返回字段 → 业务层 fileData 字段
    // 接口返回: { id, file_name, file_extension, file_size, file_path, ... }
    // 出参: { file_id, file_type, file_name, file_extension, file_size }
    // 兼容两种入参：上传接口原始返回（id）或已映射对象（file_id）
    const normalizeFile = (f) => {
      const ext = f.file_extension ?? f.extension ?? "";
      return {
        file_id: f.file_id ?? f.id,
        file_type: f.file_type ?? getFileType(ext),
        file_name: f.file_name ?? "",
        file_extension: ext,
        file_size: f.file_size ?? 0,
      };
    };

    // 编辑回显：根据附件 id 串拉附件信息，直接渲染进 van-uploader
    // 图片下载 base64 作缩略图/全屏预览，非图片显示文件名+默认图标
    const loadExistingFiles = async (ids) => {
      fileLoading.value = true;
      try {
        const headers = {
          Authorization: localStorage.getItem("Token") || "",
          tenant_id: localStorage.getItem("tenant_id") || "",
        };
        const res = await axios.post(
          "/oa_portal/oa/portal/Common/GetFilelist",
          ids,
          { headers }
        );
        if (!res.data || res.data.code !== 200 || !Array.isArray(res.data.data)) {
          Toast(res.data?.msg || "附件加载失败");
          return;
        }
        const metaList = res.data.data;
        // 业务数据（提交用）：接口原始对象含 id/file_name/extension/file_size，normalizeFile 做字段映射
        fileData.value = metaList.map((item) => normalizeFile(item));
        // uploader 展示数据
        const items = await Promise.all(
          metaList.map(async (item) => {
            const ext = String(item.extension || "").toLowerCase().replace(/^\./, "");
            const isImg = IMG_EXTS.includes(ext);
            const uiItem = {
              id: item.id,
              file_name: item.file_name,
              file_size: item.file_size,
              isImage: isImg,
              status: "done",
              deletable: true,
            };
            if (isImg) {
              try {
                const imgRes = await axios.post(
                  "/oa_portal/oa/portal/Common/DownloadBase64",
                  [item.id],
                  { headers }
                );
                const base64 = imgRes.data?.data?.[0]?.base64;
                if (base64) {
                  uiItem.content = `data:image/${ext};base64,${base64}`;
                }
              } catch (e) {
                console.log("附件缩略图加载失败", item.id, e);
              }
            }
            return uiItem;
          })
        );
        fileList.value = items;
      } catch (e) {
        console.log(e);
        Toast("附件加载失败");
      } finally {
        fileLoading.value = false;
      }
    };

    return {
      dialogVisible,
      form,
      isEdit,
      showStartPicker,
      showEndPicker,
      startPickerValue,
      endPickerValue,
      minDate,
      computedRepairHours,
      fileList,
      fileLoading,
      onAmountBlur,
      onStartConfirm,
      onEndConfirm,
      onSubmit,
      onUploadFile,
      onDeleteFile,
    };
  },
};
</script>

<style scoped lang="scss">
.repair-extend-dialog {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
}

.re-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #ebedf0;
  flex-shrink: 0;

  .re-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #86909c;
    cursor: pointer;

    &:active {
      opacity: 0.6;
    }
  }

  .re-title {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
  }

  .re-placeholder {
    width: 32px;
  }
}

.re-body {
  flex: 1;
  overflow-y: auto;
  background: #f7f8fa;
}

.re-footer {
  flex-shrink: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #ebedf0;
}

.re-upload-row {
  padding: 12px 16px;
  background: #fff;

  .re-upload-label {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #1d2129;
    margin-bottom: 8px;
  }

  .re-file-loading {
    margin-left: 8px;
  }

  // 非图片附件在 uploader 缩略图上显示文件名
  .re-file-name {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 4px 6px;
    font-size: 10px;
    line-height: 1.3;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
  }
}
</style>
